import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Server, Users, Music2, Activity, ArrowUpRight, Radio } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Counter } from "./motion";

const CELLS = [
  { key: "servers", label: "Servers", icon: Server, suffix: "", decimals: 0 },
  { key: "users", label: "Listeners", icon: Users, suffix: "", decimals: 0 },
  { key: "songs_played", label: "Songs played", icon: Music2, suffix: "", decimals: 0 },
  { key: "uptime", label: "Uptime", icon: Activity, suffix: "%", decimals: 1 },
];

export default function LivePulse() {
  const { site, stats, statsLiveAt, botOnline } = useSite();
  const tiltRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMouseMove = (e) => {
    const rect = tiltRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * -8, y: x * 10 });
  };

  const uptime = typeof stats?.uptime === "number" ? stats.uptime : 0;
  const inviteUrl = site?.bot_invite_url || "/premium";
  const version = site?.bot?.version || "";

  return (
    <motion.div
      ref={tiltRef}
      onMouseMove={onMouseMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      animate={{ rotateX: tilt.x, rotateY: tilt.y }}
      transition={{ type: "spring", stiffness: 120, damping: 16 }}
      style={{ transformStyle: "preserve-3d", perspective: 900 }}
      className="card-beam rounded-2xl bg-panel/90 backdrop-blur-md p-6 sm:p-8 w-full max-w-md shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      data-testid="live-pulse-card"
    >
      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${botOnline ? "bg-emerald-400" : "bg-red-400"}`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${botOnline ? "bg-emerald-400" : "bg-red-400"}`} />
          </span>
          <span className={`font-mono text-[11px] uppercase tracking-[0.25em] ${botOnline ? "text-white/50" : "text-red-400"}`} data-testid="live-pulse-status">
            {botOnline ? "Live network" : "Bot offline"}
          </span>
        </div>
        <div className="flex items-center gap-2 text-white/40">
          <Radio size={14} className="text-volt" />
          <span className="font-mono text-[11px]" data-testid="live-pulse-version">{version}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="live-pulse-stats">
        {CELLS.map(({ key, label, icon: Icon, suffix, decimals }) => (
          <div key={key} className="rounded-xl border border-white/5 bg-white/[0.03] px-4 py-4 hover:border-blurple/40 transition-colors duration-200">
            <Icon size={16} className="text-blurple mb-2.5" />
            <p className="font-display text-xl sm:text-2xl text-white" style={{ fontWeight: 700 }} data-testid={`live-pulse-${key}`}>
              {stats ? <Counter value={stats[key] ?? 0} suffix={suffix} decimals={decimals} /> : "—"}
            </p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-[0.18em] text-white/40">30-day availability</span>
          <span className="font-mono text-xs text-emerald-400" data-testid="live-pulse-uptime">{uptime.toFixed(1)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-blurple to-emerald-400"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, uptime)}%` }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          />
        </div>
      </div>

      <div className="mt-7 flex items-center justify-between gap-3">
        <p className="text-xs text-white/40 leading-snug" data-testid="live-pulse-sync">
          {!botOnline
            ? <>Bot is offline right now.{statsLiveAt ? <> Last sync {new Date(statsLiveAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.</> : null}<br />These are the last reported numbers.</>
            : statsLiveAt
              ? <>Last bot sync {new Date(statsLiveAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}<br />Numbers update every 5 minutes.</>
              : <>Waiting for the bot's first sync.<br />Run the patched bot to go live.</>}
        </p>
        {inviteUrl.startsWith("http") ? (
          <a href={inviteUrl} target="_blank" rel="noreferrer" data-testid="live-pulse-invite"
             className="inline-flex items-center gap-1.5 bg-blurple hover:bg-[#4752c4] text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors duration-200 shrink-0">
            Add to Discord <ArrowUpRight size={14} />
          </a>
        ) : (
          <Link to={inviteUrl} data-testid="live-pulse-invite"
                className="inline-flex items-center gap-1.5 bg-blurple hover:bg-[#4752c4] text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors duration-200 shrink-0">
            Add to Discord <ArrowUpRight size={14} />
          </Link>
        )}
      </div>
    </motion.div>
  );
}
